import { useRef, useState, useEffect, useCallback, FC } from 'react';
import { useSelector } from 'react-redux';
import { trackChatOpen } from './helpers/tracking';
import { SESSION_CHAT_STATE_KEY } from './constants';
import { localStorage } from 'dibs-browser-storage';
import { graphql, useFragment } from 'react-relay';

// components
import { BuyerChatRespLazy } from 'dibs-buyer-chat/exports/BuyerChatRespLazy';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

// types
import { PdpRespDealerChatLoader_viewer$key as Viewer } from './__generated__/PdpRespDealerChatLoader_viewer.graphql';

type Props = {
    viewer: Viewer;
};

type RelayVariables = {
    isInteractivePdpChat: boolean;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

const isMinimizedLocalStorage = (): boolean => {
    const { isMinimized = false } =
        (localStorage.getItem(SESSION_CHAT_STATE_KEY) as { isMinimized?: boolean }) || {};
    return isMinimized;
};

const viewerFragment = graphql`
    fragment PdpRespDealerChatLoader_viewer on Viewer {
        ...BuyerChatRespLazy_viewer @include(if: $isInteractivePdpChat)
    }
`;

export const PdpRespDealerChatLoader: FC<Props> = ({ viewer: viewerRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const chatVisibilityRef = useRef(null);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isChatVisible, setIsChatVisible] = useState(false);

    const { isInteractivePdpChat } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );

    useEffect(() => {
        if (isChatVisible && isInteractivePdpChat) {
            const isInitiallyMinimized = isMinimizedLocalStorage();
            trackChatOpen(isInitiallyMinimized);
        }
    }, [isChatVisible, isInteractivePdpChat]);

    useEffect(() => {
        const isInitiallyMinimized = isMinimizedLocalStorage();
        setIsMinimized(isInitiallyMinimized);
    }, []);

    const handleVisibilityChange = useCallback(({ isVisible, boundingClientRect, disconnect }) => {
        if (isVisible || boundingClientRect?.top < 0) {
            setIsChatVisible(true);
            disconnect();
        }
    }, []);

    return (
        <>
            <div ref={chatVisibilityRef} />
            <VisibilityTracker
                elementRef={chatVisibilityRef}
                onVisibilityChange={handleVisibilityChange}
            />
            {isChatVisible && isInteractivePdpChat && (
                <BuyerChatRespLazy viewer={viewer} isInitiallyMinimized={isMinimized} />
            )}
        </>
    );
};
